const benignResizeObserverWarningPattern = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i;
const startTimeUndefinedPattern =
    /Cannot read propert(?:y|ies) of (?:undefined|null) \(reading ['"]startTime['"]\)|Cannot read property ['"]startTime['"] of (?:undefined|null)/i;
const appScriptFramePattern =
    /webpack:\/\/|ClientApp\/|\bhttps?:\/\/[^\s)'"]+\/[^\s)'"]*\.js(?:\?|:|\s|$|\))|\b(?:main|app|vendor)[^\s)]*\.js:\d+|\.[tj]sx:\d+/i;

function collectStrings(value: unknown, acc: string[] = [], seen = new WeakSet<object>()): string[] {
    if (typeof value === 'string') {
        acc.push(value);
        return acc;
    }

    if (value instanceof Error) {
        acc.push(value.name, value.message);
        if (value.stack) {
            acc.push(value.stack);
        }
        return acc;
    }

    if (!value || typeof value !== 'object') {
        return acc;
    }

    if (seen.has(value)) {
        return acc;
    }

    seen.add(value);

    if (Array.isArray(value)) {
        value.forEach(item => collectStrings(item, acc, seen));
        return acc;
    }

    Object.values(value as Record<string, unknown>).forEach(item => collectStrings(item, acc, seen));
    return acc;
}

function isBenignResizeObserverNoise(strings: string[]): boolean {
    return strings.some(item => benignResizeObserverWarningPattern.test(item));
}

function looksLikeAppScriptFrame(combined: string): boolean {
    return appScriptFramePattern.test(combined);
}

function getTopStackFrame(strings: string[]): string {
    for (const item of strings) {
        const match = item.match(/^\s*at\s+[^\n]+/m);
        if (match) {
            return match[0];
        }
    }

    return '';
}

function isBenignDevToolsStartTimeNoise(strings: string[]): boolean {
    const combined = strings.join('\n');
    if (!startTimeUndefinedPattern.test(combined)) {
        return false;
    }

    // Judge the throw site (first `at` frame), not the whole stack. Catching the
    // error in our scheduler wrapper adds a main.js frame and would otherwise
    // look like an application exception.
    const topFrame = getTopStackFrame(strings);
    if (topFrame) {
        return !looksLikeAppScriptFrame(topFrame);
    }

    const standaloneAppScript = strings.some(item => !item.includes('\n') && looksLikeAppScriptFrame(item));
    if (standaloneAppScript) {
        return false;
    }

    return true;
}

export function containsBenignBrowserNoise(value: unknown): boolean {
    const strings = collectStrings(value);
    return isBenignResizeObserverNoise(strings) || isBenignDevToolsStartTimeNoise(strings);
}

export function isBenignBrowserErrorEvent(errorEvent: ErrorEvent): boolean {
    const error = errorEvent.error as { message?: string; stack?: string } | undefined;

    return containsBenignBrowserNoise({
        message: errorEvent.message,
        errorMessage: error?.message,
        stack: error?.stack,
        filename: errorEvent.filename
    });
}

export function isBenignBrowserRejection(reason: unknown): boolean {
    return containsBenignBrowserNoise(reason);
}

const CONSOLE_FILTER_INSTALLED_KEY = '__lcBenignBrowserConsoleFilterInstalled';
const SCHEDULER_GUARD_INSTALLED_KEY = '__lcBenignBrowserSchedulerGuardInstalled';
const PERFORMANCE_OBSERVER_GUARD_INSTALLED_KEY = '__lcBenignPerformanceObserverGuardInstalled';

function swallowBenignThrow(run: () => void): void {
    try {
        run();
    } catch (error) {
        if (!containsBenignBrowserNoise(error)) {
            throw error;
        }
    }
}

function wrapCallback(callback: (...args: unknown[]) => unknown): (...args: unknown[]) => unknown {
    return function(this: unknown, ...args: unknown[]) {
        try {
            return callback.apply(this, args);
        } catch (error) {
            if (!containsBenignBrowserNoise(error)) {
                throw error;
            }

            return undefined;
        }
    };
}

function wrapTimerHandler(handler: TimerHandler): TimerHandler {
    if (typeof handler !== 'function') {
        return handler;
    }

    return wrapCallback(handler as (...args: unknown[]) => unknown) as typeof handler;
}

export function installBenignBrowserConsoleFilter(): void {
    if (typeof window === 'undefined') {
        return;
    }

    const browserWindow = window as Window & { [CONSOLE_FILTER_INSTALLED_KEY]?: boolean };
    if (browserWindow[CONSOLE_FILTER_INSTALLED_KEY]) {
        return;
    }
    browserWindow[CONSOLE_FILTER_INSTALLED_KEY] = true;

    const originalError = console.error.bind(console);
    console.error = (...args: unknown[]) => {
        if (containsBenignBrowserNoise(args)) {
            return;
        }
        originalError(...args);
    };
}

export function installBenignBrowserSchedulerGuard(): void {
    if (typeof window === 'undefined') {
        return;
    }

    const browserWindow = window as Window & { [SCHEDULER_GUARD_INSTALLED_KEY]?: boolean };
    if (browserWindow[SCHEDULER_GUARD_INSTALLED_KEY]) {
        return;
    }
    browserWindow[SCHEDULER_GUARD_INSTALLED_KEY] = true;

    // DevTools Live Metrics (web-vitals reportAllChanges) throws from a timeout / idle
    // callback after SPA soft navigations. Catching it here prevents the Uncaught log;
    // window.onerror + preventDefault is too late for Chrome's console.
    const nativeSetTimeout = window.setTimeout.bind(window);
    window.setTimeout = ((handler: TimerHandler, timeout?: number, ...args: unknown[]) =>
        nativeSetTimeout(wrapTimerHandler(handler), timeout, ...args)) as typeof setTimeout;

    const nativeSetInterval = window.setInterval.bind(window);
    window.setInterval = ((handler: TimerHandler, timeout?: number, ...args: unknown[]) =>
        nativeSetInterval(wrapTimerHandler(handler), timeout, ...args)) as typeof setInterval;

    if (typeof window.requestIdleCallback === 'function') {
        const nativeRequestIdleCallback = window.requestIdleCallback.bind(window);
        window.requestIdleCallback = ((callback: IdleRequestCallback, options?: IdleRequestOptions) =>
            nativeRequestIdleCallback(
                wrapCallback(callback as (...args: unknown[]) => unknown) as IdleRequestCallback,
                options
            )) as typeof requestIdleCallback;
    }

    if (typeof window.requestAnimationFrame === 'function') {
        const nativeRequestAnimationFrame = window.requestAnimationFrame.bind(window);
        window.requestAnimationFrame = (callback: FrameRequestCallback) =>
            nativeRequestAnimationFrame(wrapCallback(callback as (...args: unknown[]) => unknown) as FrameRequestCallback);
    }

    if (typeof window.queueMicrotask === 'function') {
        const nativeQueueMicrotask = window.queueMicrotask.bind(window);
        window.queueMicrotask = (callback: VoidFunction) =>
            nativeQueueMicrotask(wrapCallback(callback as (...args: unknown[]) => unknown) as VoidFunction);
    }
}

export function installBenignPerformanceObserverGuard(): void {
    if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
        return;
    }

    const browserWindow = window as Window & { [PERFORMANCE_OBSERVER_GUARD_INSTALLED_KEY]?: boolean };
    if (browserWindow[PERFORMANCE_OBSERVER_GUARD_INSTALLED_KEY]) {
        return;
    }
    browserWindow[PERFORMANCE_OBSERVER_GUARD_INSTALLED_KEY] = true;

    const NativeObserver = window.PerformanceObserver;

    const WrappedObserver = function(
        this: PerformanceObserver,
        callback: PerformanceObserverCallback
    ): PerformanceObserver {
        return new NativeObserver((list, observer) => {
            swallowBenignThrow(() => callback(list, observer));
        });
    } as unknown as typeof PerformanceObserver;

    WrappedObserver.prototype = NativeObserver.prototype;
    Object.setPrototypeOf(WrappedObserver, NativeObserver);
    Object.defineProperty(WrappedObserver, 'supportedEntryTypes', {
        configurable: true,
        enumerable: true,
        get: () => NativeObserver.supportedEntryTypes
    });

    window.PerformanceObserver = WrappedObserver;
}

export function installBenignBrowserRuntimeGuards(): void {
    installBenignBrowserSchedulerGuard();
    installBenignPerformanceObserverGuard();
    installBenignBrowserConsoleFilter();
}
